import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import PlayCircleFilledOutlinedIcon from '@mui/icons-material/PlayCircleFilledOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type {
    IWorkspaceContentSubmission,
    ISubmissionProject,
    IRoundPayout,
} from '../../../redux/buzzbridge/campaignWorkspace.types';
import type { IDeliverableRound } from '../../../redux/buzzbridge/types';
import { DELIVERABLE_TYPE_LABELS } from '../../../utils/deliverableLabels';
import {
    approveDeliverableSubmission,
    bulkRequestRevision,
    requestDeliverableUpdate,
    requestLiveUrlRevision,
    payoutEscrowRound,
    getCampaignWorkspace,
} from '../../../redux/buzzbridge/actions';
import {
    approvingDeliverableSelector,
    bulkRequestingRevisionSelector,
    requestingDeliverableUpdateSelector,
    requestingLiveUrlRevisionSelector,
    payingOutRoundSelector,
} from '../../../redux/buzzbridge/selectors';
import { DialogueModal } from '../../../components/DialogueModal/DialogueModal';
import { VideoModalPlayer } from './VideoModalPlayer';
import { LiveLinkWindowBanner } from './LiveLinkWindowBanner';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_LUXE_ERROR,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from '../brandDashboardTokens';
import { downloadUrlAsFile } from '../../../utils/downloadFile';
import { InstagramPostAutomationModal } from '../../../components/InstagramAutomation/InstagramPostAutomationModal';
import { usePlanEntitlementGates } from '../../../hook/usePlanEntitlementGates';
import { setSubscription } from '../../../redux/subscription/actions';
import { fetchCampaignHostMine, fetchCampaignHostPricing } from '../../../redux/campaignHostSubscription/actions';
import { hasActiveCampaignHostPlanSelector } from '../../../redux/campaignHostSubscription/selectors';
import type { ICampaignHostPackage } from '../../../types/global/campaignHostSubscription.types';
import type { StandaloneMediaInput } from '../../../types/instagramAutomation.types';
import { showToastMessage } from '../../../utils/AppUtils';
import { CampaignHostCheckoutDialog } from '../subscription/CampaignHostCheckoutDialog';
import { CampaignHostTierPickerModal } from '../subscription/CampaignHostTierPickerModal';
import { useCampaignHostCheckout } from '../subscription/useCampaignHostCheckout';

type CardStatus =
    | 'pending_review'
    | 'revision_requested'
    | 'awaiting_live_url'
    | 'live_url_received'
    | 'approved'
    | 'shared';

const REVIEW_STATUS: Record<CardStatus, { label: string; color: string }> = {
    pending_review: { label: 'Pending Review', color: BRAND_LUXE_PRIMARY },
    revision_requested: { label: 'Revision Requested', color: BRAND_LUXE_ERROR },
    awaiting_live_url: { label: 'Awaiting Live URL', color: BRAND_LUXE_PRIMARY },
    live_url_received: { label: 'Live URL Received', color: BRAND_LUXE_SECONDARY },
    approved: { label: 'Approved', color: BRAND_LUXE_SECONDARY },
    shared: { label: 'Shared', color: BRAND_LUXE_SECONDARY },
};

function mapApiStatus(sub: IWorkspaceContentSubmission): CardStatus {
    if (sub.status === 'shared') return 'shared';
    if (sub.status === 'approved') return sub.live_urls?.length ? 'live_url_received' : 'awaiting_live_url';
    if (sub.status === 'needs_revision') return 'revision_requested';
    return 'pending_review';
}

function isProbablyVideoUrl(url: string): boolean {
    return /\.(mp4|webm|mov|m4v|m3u8)(\?|$)/i.test(url);
}

/** Returns the best playable URL from a submission project.
 *  After shared  → hls (m3u8 stream)
 *  Before shared → download_link (direct mp4)
 */
/** Before payment (not shared): prefer download_link for preview.
 *  After payment (shared): prefer hls stream. */
function getProjectVideoUrl(project: ISubmissionProject, isPaid: boolean): string | null {
    if (isPaid) return project.hls || project.download_link || null;
    return project.download_link || project.hls || null;
}

function MediaThumbProcessing() {
    return (
        <Box
            sx={{
                flexShrink: 0,
                width: 160,
                height: 160,
                borderRadius: 1.5,
                overflow: 'hidden',
                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.25,
                border: '1px solid rgba(255,255,255,0.06)',
            }}>
            <CircularProgress size={22} thickness={3} sx={{ color: BRAND_LUXE_PRIMARY }} />
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.35)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    textAlign: 'center',
                    px: 1,
                }}>
                Uploading…
            </Typography>
        </Box>
    );
}

function endOfUtcDayMs(value?: string | null): number | null {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999);
}

function formatWindowEnd(iso: string) {
    return new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function slotsRequired(deliverable: { multiple_uploads?: { enabled?: boolean; count?: number } } | undefined) {
    if (deliverable?.multiple_uploads?.enabled) {
        const count = Number(deliverable.multiple_uploads.count);
        return Number.isFinite(count) && count > 0 ? count : 2;
    }
    return 1;
}

function deliveryStillNeedsCreator(
    submissions: {
        status?: string;
        deliverable_type?: string;
        live_urls?: string[] | null;
        slot_index?: number | null;
    }[],
    required: { type?: string; multiple_uploads?: { enabled?: boolean; count?: number } }[],
) {
    const active = submissions.filter(submission => submission && submission.status !== 'rejected');
    const defs = required.filter(item => item?.type);
    const missingUpload = defs.length
        ? defs.some(def => {
              const slots = new Set(
                  active
                      .filter(submission => submission.deliverable_type === def.type)
                      .map(submission => submission.slot_index ?? 0),
              );
              return slots.size < slotsRequired(def);
          })
        : active.length === 0;
    const needsLink = active.some(
        submission => submission.status === 'approved' && !(submission.live_urls && submission.live_urls.length),
    );
    const needsRevision = active.some(submission => submission.status === 'needs_revision');
    return missingUpload || needsLink || needsRevision;
}

async function triggerDownload(url: string, filename?: string, onProgress?: (percent: number | null) => void) {
    try {
        await downloadUrlAsFile(url, filename || url.split('/').pop()?.split('?')[0] || 'download', progress =>
            onProgress?.(progress.percent),
        );
    } catch {
        // Fallback: open in new tab if fetch/blob fails (e.g. CORS not allowed)
        onProgress?.(null);
        window.open(url, '_blank', 'noopener,noreferrer');
    }
}

export function ParticipantSubmittedWork({
    submissions,
    participantId,
    isDirectHire = false,
    brandId,
    deliverableRounds,
    roundPayouts,
    campaignId,
    requirements = [],
    activeRoundIdx: controlledRoundIdx,
    onActiveRoundChange,
    roundDeliveryOverrides,
    liveLinkWindows,
    campaignStatus,
    campaignDeliveryDate,
    campaignEndDate,
    submissionDeliveryDate,
    campaignDeliverables,
}: {
    submissions: IWorkspaceContentSubmission[];
    participantId: string;
    isDirectHire?: boolean;
    brandId?: string;
    /** Present on recurrent campaigns */
    deliverableRounds?: IDeliverableRound[];
    roundPayouts?: IRoundPayout[];
    campaignId?: string;
    /** Requirements the brand kept on this campaign. Revision reasons are limited to these. */
    requirements?: string[];
    activeRoundIdx?: number;
    onActiveRoundChange?: (idx: number) => void;
    /** Participant-level per-round delivery date overrides (from approved extension requests). */
    roundDeliveryOverrides?: { round_id: string; delivery_date: string }[];
    liveLinkWindows?: { round_id?: string | null; expires_at?: string }[];
    campaignStatus?: string;
    campaignDeliveryDate?: string | null;
    campaignEndDate?: string | null;
    submissionDeliveryDate?: string | null;
    campaignDeliverables?: { type?: string }[];
}) {
    const dispatch = useDispatch();
    const approvingDeliverable = useSelector(approvingDeliverableSelector);
    const requestingUpdate = useSelector(requestingDeliverableUpdateSelector);
    const bulkRequestingRevision = useSelector(bulkRequestingRevisionSelector);
    const requestingLiveUrlRev = useSelector(requestingLiveUrlRevisionSelector);
    const payingOutRound = useSelector(payingOutRoundSelector);
    const { requireUploadEntitlement } = usePlanEntitlementGates();
    const hasActiveHostPlan = useSelector(hasActiveCampaignHostPlanSelector);
    const [downloadTierOpen, setDownloadTierOpen] = useState(false);
    const {
        checkoutOpen: downloadCheckoutOpen,
        setCheckoutOpen: setDownloadCheckoutOpen,
        beginMonthly: beginDownloadMonthly,
        beginLtd: beginDownloadLtd,
        onPaymentMethodReady: onDownloadPaymentReady,
        checkoutTitle: downloadCheckoutTitle,
    } = useCampaignHostCheckout({
        onSubscriptionComplete: () => {
            setDownloadTierOpen(false);
            dispatch(fetchCampaignHostMine());
            if (participantId) {
                dispatch(getCampaignWorkspace({ participantId, perspective: 'brand' }));
            }
            showToastMessage('Subscription active — downloads unlocked', 'success');
        },
    });

    const isRecurrent = Boolean(deliverableRounds?.length);

    // Active round tab index (for recurrent campaigns) — can be controlled externally
    const [internalRoundIdx, setInternalRoundIdx] = useState(0);
    const activeRoundIdx = controlledRoundIdx ?? internalRoundIdx;
    const setActiveRoundIdx = (idx: number) => {
        setInternalRoundIdx(idx);
        onActiveRoundChange?.(idx);
    };

    // Round payout confirm state
    const [roundPayoutConfirmOpen, setRoundPayoutConfirmOpen] = useState(false);
    const [roundPayoutTarget, setRoundPayoutTarget] = useState<{ roundId: string; roundNumber: number } | null>(null);

    const prevPayingOutRound = useRef(payingOutRound);
    useEffect(() => {
        if (prevPayingOutRound.current && !payingOutRound) {
            setRoundPayoutConfirmOpen(false);
            setRoundPayoutTarget(null);
        }
        prevPayingOutRound.current = payingOutRound;
    }, [payingOutRound]);

    // For recurrent: filter submissions to the active round
    const activeRound = isRecurrent ? deliverableRounds![activeRoundIdx] : null;
    const visibleSubmissions =
        isRecurrent && activeRound
            ? submissions.filter(s =>
                  s.deliverable_round_id
                      ? s.deliverable_round_id === activeRound._id
                      : s.round_number === activeRound.round_number,
              )
            : submissions;

    const deliveryDeadlineMs = isRecurrent
        ? endOfUtcDayMs(
              roundDeliveryOverrides?.find(o => String(o.round_id) === String(activeRound?._id))?.delivery_date ||
                  activeRound?.delivery_date,
          )
        : endOfUtcDayMs(submissionDeliveryDate || campaignDeliveryDate || campaignEndDate);
    const requiredDeliverables = (
        activeRound?.deliverables?.length ? activeRound.deliverables : campaignDeliverables || []
    ) as { type?: string; multiple_uploads?: { enabled?: boolean; count?: number } }[];
    const deliveryNeedsCreator = deliveryStillNeedsCreator(visibleSubmissions, requiredDeliverables);
    const deliveryDatePassed = deliveryDeadlineMs != null && Date.now() > deliveryDeadlineMs;
    const openWindow = (liveLinkWindows || []).find(w =>
        activeRound?._id ? String(w.round_id) === String(activeRound._id) : !w.round_id,
    );
    const windowStillOpen = Boolean(openWindow?.expires_at && new Date(openWindow.expires_at).getTime() > Date.now());
    const showLiveLinkGrant =
        !isDirectHire && deliveryNeedsCreator && deliveryDatePassed && campaignStatus !== 'closed';

    // Per-round payability
    const isRoundPaid = (round: IDeliverableRound) =>
        Boolean(roundPayouts?.some(p => p.round_id === round._id || p.round_number === round.round_number));

    const isRoundPayable = (round: IDeliverableRound) => {
        if (isRoundPaid(round)) return false;
        const roundSubs = submissions.filter(s =>
            s.deliverable_round_id ? s.deliverable_round_id === round._id : s.round_number === round.round_number,
        );
        if (!roundSubs.length) return false;
        return roundSubs.every(s => {
            const approved = s.status === 'approved' || s.status === 'shared';
            return approved && (isDirectHire || (s.live_urls?.length ?? 0) > 0);
        });
    };

    const handleRoundPayout = (round: IDeliverableRound) => {
        if (!round._id || !campaignId) return;
        setRoundPayoutTarget({ roundId: round._id, roundNumber: round.round_number });
        setRoundPayoutConfirmOpen(true);
    };

    const handleConfirmRoundPayout = () => {
        if (!roundPayoutTarget || !campaignId) return;
        dispatch(
            payoutEscrowRound({
                campaignId,
                participant_id: participantId,
                round_id: roundPayoutTarget.roundId,
            }),
        );
    };

    const [activeSubmissionId, setActiveSubmissionId] = useState<string | undefined>();
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const [indicator, setIndicator] = useState({ left: 0, width: 0 });
    const [approveOpen, setApproveOpen] = useState(false);
    const [revisionOpen, setRevisionOpen] = useState(false);
    const [revisionForAll, setRevisionForAll] = useState(false);
    const [revisionText, setRevisionText] = useState('');
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
    const [liveUrlRevisionOpen, setLiveUrlRevisionOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewProjectIdx, setPreviewProjectIdx] = useState(0);
    const [liveUrlRevisionText, setLiveUrlRevisionText] = useState('');
    const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
    const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [uploadMedia, setUploadMedia] = useState<StandaloneMediaInput | null>(null);
    const [uploadTitle, setUploadTitle] = useState<string | undefined>();

    // Prefer the previously selected submission id so approve/revise reloads keep the tab.
    useEffect(() => {
        if (!visibleSubmissions.length) {
            setActiveSubmissionId(undefined);
            return;
        }
        if (activeSubmissionId && visibleSubmissions.some(s => s._id === activeSubmissionId)) return;
        setActiveSubmissionId(visibleSubmissions[0]?._id);
    }, [visibleSubmissions, activeSubmissionId]);

    const activeIdx = (() => {
        if (!visibleSubmissions.length) return 0;
        if (activeSubmissionId) {
            const idx = visibleSubmissions.findIndex(s => s._id === activeSubmissionId);
            if (idx >= 0) return idx;
        }
        return 0;
    })();

    const selectSubmissionTab = (idx: number) => {
        const id = visibleSubmissions[idx]?._id;
        if (id) setActiveSubmissionId(id);
    };

    useEffect(() => {
        dispatch(setSubscription());
        dispatch(fetchCampaignHostMine());
    }, [dispatch]);

    const openDirectUpload = (proj: ISubmissionProject, index: number) => {
        if (!requireUploadEntitlement()) return;
        const mediaUrl = proj.download_link || proj.hls;
        if (!mediaUrl) {
            showToastMessage('No downloadable media available for upload.', 'error');
            return;
        }
        if (!brandId) {
            showToastMessage('Connect this brand’s Instagram, TikTok, or YouTube to upload.', 'warning');
            return;
        }
        setUploadMedia({
            media_url: mediaUrl,
            media_type: 'video',
        });
        setUploadTitle(`File ${index + 1}`);
        setUploadModalOpen(true);
    };

    const prevApproving = useRef(approvingDeliverable);
    const prevRequesting = useRef(requestingUpdate);
    const prevBulkRequesting = useRef(bulkRequestingRevision);
    const prevRequestingLiveUrlRev = useRef(requestingLiveUrlRev);

    // Close modals when their respective loading state resolves
    useEffect(() => {
        if (prevApproving.current && !approvingDeliverable) setApproveOpen(false);
        prevApproving.current = approvingDeliverable;
    }, [approvingDeliverable]);

    useEffect(() => {
        if (prevRequesting.current && !requestingUpdate) {
            setRevisionOpen(false);
            setRevisionText('');
            setSelectedReasons([]);
            setRevisionForAll(false);
        }
        prevRequesting.current = requestingUpdate;
    }, [requestingUpdate]);

    useEffect(() => {
        if (prevBulkRequesting.current && !bulkRequestingRevision) {
            setRevisionOpen(false);
            setRevisionText('');
            setSelectedReasons([]);
            setRevisionForAll(false);
        }
        prevBulkRequesting.current = bulkRequestingRevision;
    }, [bulkRequestingRevision]);

    useEffect(() => {
        if (prevRequestingLiveUrlRev.current && !requestingLiveUrlRev) {
            setLiveUrlRevisionOpen(false);
            setLiveUrlRevisionText('');
        }
        prevRequestingLiveUrlRev.current = requestingLiveUrlRev;
    }, [requestingLiveUrlRev]);

    useEffect(() => {
        // Small timeout lets the browser paint the buttons before measuring
        const id = setTimeout(() => {
            const el = tabRefs.current[activeIdx];
            if (!el) return;
            setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
        }, 0);
        return () => clearTimeout(id);
    }, [activeIdx, visibleSubmissions.length]);

    if (submissions.length === 0) return <EmptyState />;

    const active = visibleSubmissions[activeIdx] ?? visibleSubmissions[0];

    // When a round is selected but has no submissions yet, show the empty state inline
    if (isRecurrent && !active) {
        return (
            <Box>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 20,
                        color: '#fff',
                        letterSpacing: '-0.01em',
                        mb: 2,
                    }}>
                    Submitted Work
                </Typography>
                <Box
                    sx={{
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.04)',
                        overflow: 'hidden',
                    }}>
                    {isRecurrent && deliverableRounds && deliverableRounds.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                overflowX: 'auto',
                                borderBottom: '1px solid rgba(255,255,255,0.08)',
                                px: 2,
                                bgcolor: 'rgba(255,255,255,0.02)',
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                            }}>
                            {deliverableRounds.map((round, idx) => {
                                return (
                                    <Button
                                        key={round._id ?? idx}
                                        disableRipple
                                        onClick={() => {
                                            setActiveRoundIdx(idx);
                                            setActiveSubmissionId(undefined);
                                        }}
                                        sx={{
                                            flexShrink: 0,
                                            pb: 1.5,
                                            pt: 1.5,
                                            px: 0,
                                            mr: 3,
                                            minWidth: 'auto',
                                            borderRadius: 0,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 12,
                                            textTransform: 'none',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            color:
                                                idx === activeRoundIdx ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.4)',
                                            borderBottom:
                                                idx === activeRoundIdx
                                                    ? `2px solid ${BRAND_LUXE_PRIMARY}`
                                                    : '2px solid transparent',
                                            '&:hover': { bgcolor: 'transparent' },
                                        }}>
                                        Delivery {round.round_number}
                                    </Button>
                                );
                            })}
                        </Box>
                    )}
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography
                            sx={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            No submissions yet for this round.
                        </Typography>
                        {(() => {
                            const activeRound = deliverableRounds?.[activeRoundIdx];
                            const override = roundDeliveryOverrides?.find(
                                o => String(o.round_id) === String(activeRound?._id),
                            );
                            const rawDate = override?.delivery_date || activeRound?.delivery_date;
                            const effectiveDate = rawDate
                                ? new Date(rawDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                  })
                                : null;
                            return effectiveDate ? (
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: override ? 'rgba(255,201,101,0.7)' : 'rgba(255,255,255,0.25)',
                                        mt: 1,
                                    }}>
                                    Due {effectiveDate}
                                </Typography>
                            ) : null;
                        })()}
                    </Box>
                </Box>
            </Box>
        );
    }
    const rawCardStatus = mapApiStatus(active);
    // For direct hire: approved submissions don't need a live URL — show "Approved" instead
    const cardStatus = isDirectHire && rawCardStatus === 'awaiting_live_url' ? 'approved' : rawCardStatus;
    const status = REVIEW_STATUS[cardStatus];
    const isPaid = active.status === 'shared';
    const projects = active.projects ?? [];
    const submittedDate = active.submitted_at
        ? new Date(active.submitted_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : null;

    const openPreview = (idx = 0) => {
        setPreviewProjectIdx(idx);
        setPreviewOpen(true);
    };

    const handleApprove = () => {
        if (!active._id) return;
        dispatch(approveDeliverableSubmission({ deliverableId: active._id, participantId }));
    };

    const requirementOptions = (requirements || []).map(line => String(line || '').trim()).filter(Boolean);
    const mustPickReasons = requirementOptions.length > 0;
    const pickedReasons = requirementOptions.filter(line => selectedReasons.includes(line));
    const revisionComment = revisionText.trim();
    const revisionFeedback = mustPickReasons
        ? pickedReasons.length
            ? revisionComment
                ? `${pickedReasons.join('\n')}\n\n${revisionComment}`
                : pickedReasons.join('\n')
            : ''
        : revisionComment;
    const canSubmitRevision = mustPickReasons ? pickedReasons.length > 0 : Boolean(revisionComment);

    const handleRevisionSubmit = () => {
        if (!canSubmitRevision) return;
        if (revisionForAll) {
            dispatch(
                bulkRequestRevision({
                    participantId,
                    feedback: revisionFeedback,
                    reasons: mustPickReasons ? pickedReasons : undefined,
                    comment: mustPickReasons && revisionComment ? revisionComment : undefined,
                }),
            );
        } else {
            if (!active._id) return;
            dispatch(
                requestDeliverableUpdate({
                    submissionId: active._id,
                    feedback: revisionFeedback,
                    participantId,
                    reasons: mustPickReasons ? pickedReasons : undefined,
                    comment: mustPickReasons && revisionComment ? revisionComment : undefined,
                }),
            );
        }
    };

    const handleLiveUrlRevisionSubmit = () => {
        if (!active._id || !liveUrlRevisionText.trim()) return;
        dispatch(
            requestLiveUrlRevision({ participantId, submissionId: active._id, feedback: liveUrlRevisionText.trim() }),
        );
    };

    return (
        <Box>
            <Typography
                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: '#fff', letterSpacing: '-0.01em', mb: 2 }}>
                Submitted Work
            </Typography>

            <Box
                sx={{
                    bgcolor: BRAND_SURFACE_CONTAINER,
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.04)',
                    overflow: 'hidden',
                }}>
                {/* Round selector strip (recurrent campaigns only) */}
                {isRecurrent && deliverableRounds && deliverableRounds.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            overflowX: 'auto',
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            px: 2,
                            bgcolor: 'rgba(255,255,255,0.02)',
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                        }}>
                        {deliverableRounds.map((round, idx) => {
                            const paid = isRoundPaid(round);
                            const payable = isRoundPayable(round);
                            const isActiveRound = idx === activeRoundIdx;
                            return (
                                <Button
                                    key={round._id ?? idx}
                                    disableRipple
                                    onClick={() => {
                                        setActiveRoundIdx(idx);
                                        setActiveSubmissionId(undefined);
                                    }}
                                    sx={{
                                        flexShrink: 0,
                                        pb: 1.5,
                                        pt: 1.5,
                                        px: 0,
                                        mr: 3,
                                        minWidth: 'auto',
                                        borderRadius: 0,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 12,
                                        textTransform: 'none',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        color: isActiveRound ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.4)',
                                        borderBottom: isActiveRound
                                            ? `2px solid ${BRAND_LUXE_PRIMARY}`
                                            : '2px solid transparent',
                                        transition: 'color 0.2s, border-color 0.2s',
                                        '&:hover': {
                                            bgcolor: 'transparent',
                                            color: isActiveRound ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.7)',
                                        },
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        Delivery {round.round_number}
                                        {round.label ? ` · ${round.label}` : ''}
                                        {paid && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    ml: 0.5,
                                                    fontSize: 9,
                                                    px: 0.75,
                                                    py: 0.25,
                                                    borderRadius: 999,
                                                    bgcolor: 'rgba(78,205,196,0.15)',
                                                    color: BRAND_LUXE_SECONDARY,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    letterSpacing: '0.06em',
                                                    textTransform: 'uppercase',
                                                }}>
                                                Paid
                                            </Box>
                                        )}
                                        {!paid && payable && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    ml: 0.5,
                                                    fontSize: 9,
                                                    px: 0.75,
                                                    py: 0.25,
                                                    borderRadius: 999,
                                                    bgcolor: 'rgba(255,201,101,0.15)',
                                                    color: BRAND_LUXE_PRIMARY,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    letterSpacing: '0.06em',
                                                    textTransform: 'uppercase',
                                                }}>
                                                Ready
                                            </Box>
                                        )}
                                    </Box>
                                </Button>
                            );
                        })}
                    </Box>
                )}

                <LiveLinkWindowBanner
                    show={showLiveLinkGrant}
                    windowStillOpen={windowStillOpen}
                    expiresLabel={openWindow?.expires_at ? formatWindowEnd(openWindow.expires_at) : undefined}
                    participantId={participantId}
                    roundId={activeRound?._id || null}
                />

                {/* Submission Tab strip */}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        overflowX: 'auto',
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        px: 2,
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                    }}>
                    {visibleSubmissions.map((s, idx) => {
                        const cs = mapApiStatus(s);
                        const st = REVIEW_STATUS[cs];
                        const isActive = idx === activeIdx;
                        return (
                            <Button
                                key={s._id ?? idx}
                                ref={el => {
                                    tabRefs.current[idx] = el;
                                }}
                                disableRipple
                                onClick={() => selectSubmissionTab(idx)}
                                sx={{
                                    flexShrink: 0,
                                    pb: 1.75,
                                    pt: 1.75,
                                    px: 0,
                                    mr: 3,
                                    minWidth: 'auto',
                                    borderRadius: 0,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    textTransform: 'none',
                                    letterSpacing: 0,
                                    color: isActive ? '#fff' : 'rgba(255,255,255,0.4)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    transition: 'color 0.2s',
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                        color: isActive ? '#fff' : 'rgba(255,255,255,0.75)',
                                    },
                                }}>
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: st.color,
                                        flexShrink: 0,
                                        boxShadow: isActive ? `0 0 5px ${st.color}` : 'none',
                                        transition: 'box-shadow 0.2s',
                                    }}
                                />
                                {(s.deliverable_type &&
                                    (DELIVERABLE_TYPE_LABELS[s.deliverable_type] ?? s.deliverable_type)) ||
                                    `Submission ${idx + 1}`}
                            </Button>
                        );
                    })}
                    {/* Sliding indicator */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: indicator.left,
                            width: indicator.width,
                            height: 2,
                            bgcolor: BRAND_LUXE_PRIMARY,
                            borderRadius: '2px 2px 0 0',
                            transition:
                                'left 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    />
                </Box>

                {/* Panel body */}
                <Box sx={{ p: 2.5 }}>
                    {/* Status + meta row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                            {submittedDate && (
                                <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                                    Submitted {submittedDate}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', mt: 0.25 }}>
                                {projects.length} / {active.expected_count ?? projects.length} file
                                {(active.expected_count ?? projects.length) !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    bgcolor: status.color,
                                    boxShadow: `0 0 6px ${status.color}80`,
                                }}
                            />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 10,
                                    color: status.color,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                }}>
                                {status.label}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Horizontally scrollable media strip */}
                    {projects.length > 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                overflowX: 'auto',
                                pb: 0.5,
                                '&::-webkit-scrollbar': { height: 3 },
                                '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                '&::-webkit-scrollbar-thumb': {
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    borderRadius: 999,
                                },
                            }}>
                            {projects.map((proj, i) => {
                                if (proj.processing_complete === false) {
                                    return <MediaThumbProcessing key={proj._id ?? i} />;
                                }
                                const videoUrl = getProjectVideoUrl(proj, isPaid);
                                const thumb = proj.thumbnail ?? proj.feature_image ?? null;
                                const isVid = !!(videoUrl && isProbablyVideoUrl(videoUrl));
                                return (
                                    <MediaThumb
                                        key={proj._id ?? i}
                                        url={videoUrl ?? thumb ?? ''}
                                        thumbnail={thumb ?? undefined}
                                        isVideo={isVid}
                                        onClick={() => openPreview(i)}
                                    />
                                );
                            })}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                height: 140,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                borderRadius: 1.5,
                            }}>
                            <ImageOutlinedIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.12)' }} />
                        </Box>
                    )}

                    {/* Downloads — BE omits download_link unless direct hire or host has an active plan */}
                    {(() => {
                        const downloadableProjects = projects.filter(p => p.download_link || p.hls);
                        const canDownload = isDirectHire || hasActiveHostPlan;
                        if (!downloadableProjects.length) return null;
                        const openDownloadSubscribe = () => {
                            dispatch(fetchCampaignHostPricing());
                            setDownloadTierOpen(true);
                        };
                        return (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 1.5,
                                    borderRadius: 1.5,
                                    border: '1px solid rgba(255,255,255,0.07)',
                                    bgcolor: 'rgba(255,255,255,0.03)',
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: 9,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        color: 'rgba(255,255,255,0.35)',
                                        mb: 1,
                                    }}>
                                    Downloads
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    {projects.map((proj, i) => {
                                        const downloadUrl = proj.download_link;
                                        if (!downloadUrl && !proj.hls) return null;
                                        const fileKey = String(proj._id ?? i);
                                        const isDownloading = downloadingKey === fileKey;
                                        const needsSubscribe = isPaid && !isDirectHire && !downloadUrl;
                                        return (
                                            <Box key={fileKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: 'rgba(255,255,255,0.5)',
                                                        flex: 1,
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                    File {i + 1}
                                                </Typography>
                                                {downloadUrl ? (
                                                    <Button
                                                        size="small"
                                                        disabled={isDownloading || !canDownload}
                                                        onClick={() => {
                                                            if (!canDownload) {
                                                                openDownloadSubscribe();
                                                                return;
                                                            }
                                                            setDownloadingKey(fileKey);
                                                            setDownloadProgress(null);
                                                            void triggerDownload(
                                                                downloadUrl,
                                                                `file-${i + 1}.mp4`,
                                                                percent => setDownloadProgress(percent),
                                                            ).finally(() => {
                                                                setDownloadingKey(null);
                                                                setDownloadProgress(null);
                                                            });
                                                        }}
                                                        startIcon={
                                                            isDownloading ? (
                                                                <CircularProgress
                                                                    size={12}
                                                                    thickness={5}
                                                                    variant={
                                                                        typeof downloadProgress === 'number'
                                                                            ? 'determinate'
                                                                            : 'indeterminate'
                                                                    }
                                                                    value={
                                                                        typeof downloadProgress === 'number'
                                                                            ? downloadProgress
                                                                            : undefined
                                                                    }
                                                                    sx={{ color: BRAND_LUXE_PRIMARY }}
                                                                />
                                                            ) : undefined
                                                        }
                                                        sx={{
                                                            color: BRAND_LUXE_PRIMARY,
                                                            border: `1px solid ${BRAND_LUXE_PRIMARY}55`,
                                                            borderRadius: 999,
                                                            fontSize: 12,
                                                            px: 1.5,
                                                            py: 0.25,
                                                            textTransform: 'none',
                                                            flexShrink: 0,
                                                            minWidth: 0,
                                                            '&.Mui-disabled': {
                                                                color: BRAND_LUXE_PRIMARY,
                                                                opacity: 0.85,
                                                            },
                                                        }}>
                                                        {isDownloading
                                                            ? typeof downloadProgress === 'number'
                                                                ? `${downloadProgress}%`
                                                                : 'Downloading…'
                                                            : 'Download'}
                                                    </Button>
                                                ) : needsSubscribe ? (
                                                    <Button
                                                        size="small"
                                                        onClick={openDownloadSubscribe}
                                                        sx={{
                                                            color: BRAND_LUXE_PRIMARY,
                                                            border: `1px solid ${BRAND_LUXE_PRIMARY}55`,
                                                            borderRadius: 999,
                                                            fontSize: 12,
                                                            px: 1.5,
                                                            py: 0.25,
                                                            textTransform: 'none',
                                                            flexShrink: 0,
                                                            minWidth: 0,
                                                        }}>
                                                        Subscribe to download
                                                    </Button>
                                                ) : null}
                                                {isDirectHire && (
                                                    <Button
                                                        size="small"
                                                        onClick={() => openDirectUpload(proj, i)}
                                                        sx={{
                                                            color: BRAND_LUXE_SECONDARY,
                                                            border: `1px solid ${BRAND_LUXE_SECONDARY}55`,
                                                            borderRadius: 999,
                                                            fontSize: 12,
                                                            px: 1.5,
                                                            py: 0.25,
                                                            textTransform: 'none',
                                                            flexShrink: 0,
                                                            minWidth: 0,
                                                        }}>
                                                        Upload
                                                    </Button>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })()}

                    {/* Live URLs row (standard campaigns) */}
                    {!isDirectHire && (active.live_urls?.length ?? 0) > 0 && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 1.5,
                                borderRadius: 1.5,
                                border: '1px solid rgba(255,255,255,0.07)',
                                bgcolor: 'rgba(255,255,255,0.03)',
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 9,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                    color: 'rgba(255,255,255,0.35)',
                                    mb: 1,
                                }}>
                                Live {(active.live_urls?.length ?? 0) > 1 ? 'URLs' : 'URL'}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {(active.live_urls ?? []).map((url, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                        }}>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: BRAND_LUXE_SECONDARY,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1,
                                            }}>
                                            {url}
                                        </Typography>
                                        <IconButton
                                            component="a"
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            size="small"
                                            sx={{ color: BRAND_LUXE_SECONDARY, flexShrink: 0 }}>
                                            <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Action buttons */}
                    {cardStatus === 'pending_review' && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                            <Button
                                size="small"
                                onClick={() => openPreview()}
                                sx={{
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'none',
                                    color: 'rgba(255,255,255,0.55)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: 999,
                                    px: 2,
                                    py: 0.5,
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.06)',
                                        borderColor: 'rgba(255,255,255,0.22)',
                                    },
                                }}>
                                View
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    setSelectedReasons([]);
                                    setRevisionText('');
                                    setRevisionOpen(true);
                                }}
                                sx={{
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'none',
                                    color: BRAND_LUXE_ERROR,
                                    border: `1px solid ${BRAND_LUXE_ERROR}55`,
                                    borderRadius: 999,
                                    px: 2,
                                    py: 0.5,
                                    '&:hover': { bgcolor: `${BRAND_LUXE_ERROR}12`, borderColor: BRAND_LUXE_ERROR },
                                }}>
                                Revise
                            </Button>
                            <Button
                                size="small"
                                onClick={() => setApproveOpen(true)}
                                sx={{
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'none',
                                    color: BRAND_LUXE_SECONDARY,
                                    border: `1px solid ${BRAND_LUXE_SECONDARY}55`,
                                    borderRadius: 999,
                                    px: 2,
                                    py: 0.5,
                                    '&:hover': {
                                        bgcolor: `${BRAND_LUXE_SECONDARY}12`,
                                        borderColor: BRAND_LUXE_SECONDARY,
                                    },
                                }}>
                                Approve
                            </Button>
                        </Box>
                    )}
                    {cardStatus === 'live_url_received' && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                            <Button
                                size="small"
                                onClick={() => setLiveUrlRevisionOpen(true)}
                                sx={{
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'none',
                                    color: BRAND_LUXE_ERROR,
                                    border: `1px solid ${BRAND_LUXE_ERROR}55`,
                                    borderRadius: 999,
                                    px: 2,
                                    py: 0.5,
                                    '&:hover': { bgcolor: `${BRAND_LUXE_ERROR}12`, borderColor: BRAND_LUXE_ERROR },
                                }}>
                                Reject
                            </Button>
                        </Box>
                    )}

                    {/* Per-round pay button (recurrent campaigns only) */}
                    {isRecurrent &&
                        activeRound &&
                        (() => {
                            const paid = isRoundPaid(activeRound);
                            const payable = isRoundPayable(activeRound);
                            if (paid) {
                                return (
                                    <Box
                                        sx={{
                                            mt: 2.5,
                                            p: 1.5,
                                            borderRadius: 1.5,
                                            bgcolor: 'rgba(78,205,196,0.08)',
                                            border: '1px solid rgba(78,205,196,0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: BRAND_LUXE_SECONDARY,
                                            }}>
                                            Delivery {activeRound.round_number} payment released
                                        </Typography>
                                    </Box>
                                );
                            }
                            if (payable) {
                                return (
                                    <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            size="small"
                                            onClick={() => handleRoundPayout(activeRound)}
                                            sx={{
                                                fontSize: 13,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                textTransform: 'none',
                                                bgcolor: BRAND_LUXE_PRIMARY,
                                                color: '#392700',
                                                borderRadius: 999,
                                                px: 2.5,
                                                py: 0.75,
                                                '&:hover': { bgcolor: '#feb700' },
                                            }}>
                                            Pay Delivery {activeRound.round_number}
                                        </Button>
                                    </Box>
                                );
                            }
                            return null;
                        })()}
                </Box>
            </Box>

            {/* Approve confirmation modal */}
            <DialogueModal
                open={approveOpen}
                onClose={() => !approvingDeliverable && setApproveOpen(false)}
                header="Approve submission"
                buttonEnabled
                buttonText={approvingDeliverable ? '' : 'Yes, approve'}
                buttonProps={{
                    disabled: approvingDeliverable,
                    startIcon: approvingDeliverable ? (
                        <CircularProgress size={16} sx={{ color: 'inherit' }} />
                    ) : undefined,
                }}
                onSubmit={handleApprove}
                paperSX={{ maxWidth: { xs: '90%', sm: '440px' }, backgroundColor: '#1a1919' }}
                dialogueSX={{ height: 'auto', alignItems: 'center' }}>
                <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                        Are you sure you want to approve{' '}
                        <Box component="span" sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            {(active.deliverable_type &&
                                (DELIVERABLE_TYPE_LABELS[active.deliverable_type] ?? active.deliverable_type)) ||
                                'this submission'}
                        </Box>
                        ? This will mark the deliverable as complete.
                    </Typography>
                </Box>
            </DialogueModal>

            {/* Request revision modal */}
            <DialogueModal
                open={revisionOpen}
                onClose={() => !(requestingUpdate || bulkRequestingRevision) && setRevisionOpen(false)}
                header="Request revision"
                buttonEnabled
                buttonText={requestingUpdate || bulkRequestingRevision ? '' : 'Send feedback'}
                buttonProps={{
                    disabled: requestingUpdate || bulkRequestingRevision || !canSubmitRevision,
                    startIcon:
                        requestingUpdate || bulkRequestingRevision ? (
                            <CircularProgress size={16} sx={{ color: 'inherit' }} />
                        ) : undefined,
                }}
                onSubmit={handleRevisionSubmit}
                paperSX={{ maxWidth: { xs: '90%', sm: '480px' }, backgroundColor: '#1a1919' }}
                dialogueSX={{ height: 'auto', alignItems: 'center' }}>
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
                        {mustPickReasons
                            ? 'Select the saved requirements this submission missed. You can also add an optional comment.'
                            : 'Let the creator know what needs to be changed for '}
                        {!mustPickReasons ? (
                            <Box component="span" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                {revisionForAll
                                    ? 'all pending submissions'
                                    : (active.deliverable_type &&
                                          (DELIVERABLE_TYPE_LABELS[active.deliverable_type] ??
                                              active.deliverable_type)) ||
                                      'this submission'}
                            </Box>
                        ) : null}
                        {!mustPickReasons ? '.' : null}
                    </Typography>
                    {mustPickReasons ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {requirementOptions.map(line => (
                                <FormControlLabel
                                    key={line}
                                    control={
                                        <Checkbox
                                            checked={selectedReasons.includes(line)}
                                            onChange={() =>
                                                setSelectedReasons(prev =>
                                                    prev.includes(line)
                                                        ? prev.filter(item => item !== line)
                                                        : [...prev, line],
                                                )
                                            }
                                            disabled={requestingUpdate || bulkRequestingRevision}
                                            size="small"
                                            sx={{
                                                color: 'rgba(255,255,255,0.3)',
                                                '&.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                color: 'rgba(255,255,255,0.82)',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            {line}
                                        </Typography>
                                    }
                                />
                            ))}
                        </Box>
                    ) : null}
                    <TextField
                        multiline
                        rows={mustPickReasons ? 3 : 4}
                        placeholder={
                            mustPickReasons
                                ? 'Optional comment for the creator…'
                                : 'Describe what needs to be revised...'
                        }
                        value={revisionText}
                        onChange={e => setRevisionText(e.target.value)}
                        fullWidth
                        disabled={requestingUpdate || bulkRequestingRevision}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(255,255,255,0.04)',
                                borderRadius: 2,
                                color: '#fff',
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                '&.Mui-focused fieldset': { borderColor: BRAND_LUXE_PRIMARY },
                            },
                            '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
                        }}
                    />
                    {visibleSubmissions.length > 1 && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={revisionForAll}
                                    onChange={e => setRevisionForAll(e.target.checked)}
                                    disabled={requestingUpdate || bulkRequestingRevision}
                                    size="small"
                                    sx={{
                                        color: 'rgba(255,255,255,0.3)',
                                        '&.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: 'rgba(255,255,255,0.55)',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    }}>
                                    Request for all submissions
                                </Typography>
                            }
                        />
                    )}
                </Box>
            </DialogueModal>

            {/* Request live URL revision modal */}
            <DialogueModal
                open={liveUrlRevisionOpen}
                onClose={() => !requestingLiveUrlRev && setLiveUrlRevisionOpen(false)}
                header="Request live URL revision"
                buttonEnabled
                buttonText={requestingLiveUrlRev ? '' : 'Send feedback'}
                buttonProps={{
                    disabled: requestingLiveUrlRev || !liveUrlRevisionText.trim(),
                    startIcon: requestingLiveUrlRev ? (
                        <CircularProgress size={16} sx={{ color: 'inherit' }} />
                    ) : undefined,
                }}
                onSubmit={handleLiveUrlRevisionSubmit}
                paperSX={{ maxWidth: { xs: '90%', sm: '480px' }, backgroundColor: '#1a1919' }}
                dialogueSX={{ height: 'auto', alignItems: 'center' }}>
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
                        Let the creator know what's wrong with the live URL for{' '}
                        <Box component="span" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            {(active.deliverable_type &&
                                (DELIVERABLE_TYPE_LABELS[active.deliverable_type] ?? active.deliverable_type)) ||
                                'this submission'}
                        </Box>
                        .
                    </Typography>
                    <TextField
                        multiline
                        rows={4}
                        placeholder="Describe the issue with the live URL..."
                        value={liveUrlRevisionText}
                        onChange={e => setLiveUrlRevisionText(e.target.value)}
                        fullWidth
                        disabled={requestingLiveUrlRev}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(255,255,255,0.04)',
                                borderRadius: 2,
                                color: '#fff',
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                '&.Mui-focused fieldset': { borderColor: BRAND_LUXE_PRIMARY },
                            },
                            '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
                        }}
                    />
                </Box>
            </DialogueModal>

            {/* Preview modal */}
            <DialogueModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                paperSX={{
                    maxWidth: { xs: '95vw', sm: '680px', md: '860px' },
                    height: '50vh',
                    maxHeight: '50vh',
                    backgroundColor: '#0e0e0e',
                    p: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
                dialogueSX={{ alignItems: 'center', height: '100%' }}>
                <MediaPreviewPanel
                    project={projects[previewProjectIdx]}
                    isPaid={isPaid}
                    deliverableType={active.deliverable_type}
                />
            </DialogueModal>

            {isDirectHire && (
                <InstagramPostAutomationModal
                    open={uploadModalOpen}
                    onClose={() => {
                        setUploadModalOpen(false);
                        setUploadMedia(null);
                    }}
                    standaloneMedia={uploadMedia ?? undefined}
                    projectTitle={uploadTitle}
                    defaultCaption={uploadTitle ?? ''}
                    brandId={brandId}
                />
            )}

            <CampaignHostTierPickerModal
                open={downloadTierOpen}
                onClose={() => setDownloadTierOpen(false)}
                onSelectMonthly={(pkg: ICampaignHostPackage) => {
                    setDownloadTierOpen(false);
                    beginDownloadMonthly(pkg);
                }}
                onSelectLtd={(pkg: ICampaignHostPackage) => {
                    setDownloadTierOpen(false);
                    beginDownloadLtd(pkg);
                }}
            />
            <CampaignHostCheckoutDialog
                open={downloadCheckoutOpen}
                title={downloadCheckoutTitle}
                onClose={() => setDownloadCheckoutOpen(false)}
                onPaymentMethodReady={onDownloadPaymentReady}
            />

            {/* Round payout confirm modal */}
            <DialogueModal
                open={roundPayoutConfirmOpen}
                onClose={() => !payingOutRound && setRoundPayoutConfirmOpen(false)}
                header={`Release Delivery ${roundPayoutTarget?.roundNumber ?? ''} Payment?`}
                buttonEnabled
                buttonText={payingOutRound ? '' : 'Yes, Release Payment'}
                buttonProps={{
                    disabled: payingOutRound,
                    startIcon: payingOutRound ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined,
                }}
                onSubmit={handleConfirmRoundPayout}
                paperSX={{ maxWidth: { xs: '90%', sm: '440px' }, backgroundColor: '#1a1919' }}
                dialogueSX={{ height: 'auto', alignItems: 'center' }}>
                <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                        You're about to release the payment for{' '}
                        <Box component="span" sx={{ color: '#fff', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            Delivery {roundPayoutTarget?.roundNumber}
                        </Box>
                        . This action is irreversible.
                    </Typography>
                </Box>
            </DialogueModal>
        </Box>
    );
}

function MediaPreviewPanel({
    project,
    isPaid,
    deliverableType,
}: {
    project: ISubmissionProject | undefined;
    isPaid: boolean;
    deliverableType?: string;
}) {
    const videoUrl = project ? getProjectVideoUrl(project, isPaid) : null;
    const thumbnailUrl = project?.thumbnail ?? project?.feature_image ?? null;
    const isVideo = !!(videoUrl && isProbablyVideoUrl(videoUrl));
    const displayUrl = thumbnailUrl ?? videoUrl ?? '';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <Box
                sx={{
                    px: 2.5,
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    flexShrink: 0,
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                    {(deliverableType && (DELIVERABLE_TYPE_LABELS[deliverableType] ?? deliverableType)) || 'Preview'}
                </Typography>
            </Box>

            {/* Media viewer */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#000',
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: 0,
                }}>
                {isVideo && videoUrl ? (
                    <Box key={videoUrl} sx={{ width: '100%', alignSelf: 'stretch' }}>
                        <VideoModalPlayer
                            src={videoUrl}
                            videoId={project?.video_id}
                            maxVideoHeight="calc(50vh - 120px)"
                        />
                    </Box>
                ) : displayUrl ? (
                    <Box
                        component="img"
                        src={displayUrl}
                        alt=""
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                ) : (
                    <ImageOutlinedIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.15)' }} />
                )}
            </Box>
        </Box>
    );
}

function MediaThumb({
    url,
    thumbnail,
    isVideo,
    onClick,
}: {
    url: string;
    thumbnail?: string;
    isVideo?: boolean;
    onClick: () => void;
}) {
    const showAsVideo = isVideo ?? isProbablyVideoUrl(url);
    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                flexShrink: 0,
                width: 160,
                height: 160,
                borderRadius: 1.5,
                overflow: 'hidden',
                bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                cursor: 'pointer',
                '&:hover > .thumb-overlay': { opacity: 1 },
            }}>
            {thumbnail ? (
                <Box
                    component="img"
                    src={thumbnail}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            ) : showAsVideo ? (
                <Box
                    component="video"
                    src={url}
                    muted
                    playsInline
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            ) : (
                <Box
                    component="img"
                    src={url}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            )}
            <Box
                className="thumb-overlay"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.45)',
                    opacity: showAsVideo ? 1 : 0,
                    transition: 'opacity 0.15s',
                }}>
                <PlayCircleFilledOutlinedIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.85)' }} />
            </Box>
        </Box>
    );
}

function EmptyState() {
    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                borderRadius: 2,
                p: 5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                border: '2px dashed rgba(255,255,255,0.06)',
            }}>
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                <ImageOutlinedIcon sx={{ fontSize: 28, color: 'rgba(255,255,255,0.15)' }} />
            </Box>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color: 'rgba(255,255,255,0.4)' }}>
                No work submitted yet
            </Typography>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.25)',
                    textAlign: 'center',
                    maxWidth: 320,
                }}>
                Content will appear here once the creator uploads their deliverables for review.
            </Typography>
        </Box>
    );
}
